/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import PatchManager from "App/utils/globe/chunk_lod/patch_manager";
import Visitor, {VisitorOrdering} from "App/utils/globe/chunk_lod/visitor";
import SyncRequest from "App/utils/globe/chunk_lod/sync_request";
import TreeNode from "App/utils/globe/chunk_lod/tree/node";
import {ChunkLODNodeState} from "App/utils/globe/chunk_lod/tree/chunk_lod_node";
import {vec3} from "gl-matrix";

export default
class SynchronizationManager extends Visitor {
    private _patchManager: PatchManager;
    private _createGPUResourceRequestsMap: any;
    private _createGPUResourceRequestsQueue: SyncRequest[];
    private _maxPendingMemory: number;
    private _currentPendingMemory: number;
    private _view: any;
    private _patch: PatchManager;

    constructor(aPatchManager: PatchManager) {
        super();

        this._patchManager = aPatchManager;

        this._visiteeConsumerFunction = this.collectCreateResourcesRequests.bind(this);
        this._ordering = VisitorOrdering.pre;

        this._createGPUResourceRequestsMap = {};
        this._createGPUResourceRequestsQueue = [];

        this._maxPendingMemory = 20 * 1024 * 1024;
        this._currentPendingMemory = 0;

        this._view = null;
        this._patch = aPatchManager;
    }

    public setView(aView: any) {
        this._view = aView;
    }

    public collectCreateResourcesRequests(aVisitee: TreeNode) {
        let uid = aVisitee.getUID();
        if (aVisitee.getState() === ChunkLODNodeState.DATAREADY) {
            if (this._createGPUResourceRequestsMap[uid] === undefined) {
                if (aVisitee.hasValidDataPath()) {
                    let nofVertices = aVisitee.getNofVertices(aVisitee.getAddress());

                    let attributes = {
                        positions: new Uint8Array(nofVertices * 3 * 4),
                        positionsPos: 0,
                        normals: new Uint8Array(nofVertices * 3 * 4),
                        normalsPos: 0,
                        texcoords: new Uint8Array(nofVertices * 2 * 4),
                        texcoordsPos: 0,
                        layers: new Uint8Array(nofVertices * 1 * 4),
                        layersPos: 0,
                        surfacestyles: new Uint8Array(nofVertices * 1 * 4),
                        surfacestylesPos: 0
                    };

                    aVisitee.getAttributes(aVisitee.getAddress(), attributes);

                    let request = new SyncRequest(
                        uid,
                        aVisitee,
                        "commit2gpu",
                        aVisitee.getMetadata(),
                        attributes,
                        aVisitee.getIndices(),
                        aVisitee.getTextureData()
                    );
                    this._createGPUResourceRequestsMap[uid] = request;
                    this._createGPUResourceRequestsQueue.push(request);
                }
            }
        }
    }

    public settledOnGPU(aMessage: any) {
        let request = this._createGPUResourceRequestsMap[aMessage.uid];
        if (request) {
            this._currentPendingMemory -= request.getSize();
            this.removeRequest(request);
            request.node.setState(ChunkLODNodeState.SETTLEDONGPU);
        }
    }

    public removeRequest(aRequest) {
        delete this._createGPUResourceRequestsMap[aRequest.uid];

        for (let j = 0; j < this._createGPUResourceRequestsQueue.length; j++) {
            if (this._createGPUResourceRequestsQueue[j].getUID() === aRequest.uid) {
                this._createGPUResourceRequestsQueue.splice(j, 1);
                return;
            }
        }
    }

    public update(aNodesToRemove) {
        let worker = this._patchManager.getWorker();

        let priorityA = null;
        let priorityB = null;

        let levelA = 0;
        let levelB = 0;

        // sort by level i.e. the coarsest levels shall be synchronized first
        this._createGPUResourceRequestsQueue.sort((a, b): number => {

            priorityA = this._patch.getPriorityManager().getPriority(a.node);
            priorityB = this._patch.getPriorityManager().getPriority(b.node);

            levelA = a.node.getAddress().level;
            levelB = b.node.getAddress().level;

            // lower level higher priority
            if (levelB != levelA) {
                // return levelA - levelB;
            }

            if (priorityA !== priorityB) {
                return priorityB - priorityA;
            }

            // higher distance, lower priority
            return vec3.distance(this._view.eye, b.node.getCenter()) - vec3.distance(this._view.eye, a.node.getCenter());
        });

        for (let i = 0; i < this._createGPUResourceRequestsQueue.length; i++) {
            let request = this._createGPUResourceRequestsQueue[i];

            if (request.node.getState() === ChunkLODNodeState.DATAREADY) {
                if (this._patch.getWorker().getUpdatesLimited() && this._currentPendingMemory + request.getSize() > this._maxPendingMemory && this._currentPendingMemory > 0) {
                    break;
                }

                request.node.setState(ChunkLODNodeState.COMMITTEDTOGPU);

                let message = {
                    uid: request.getUID(),
                    priority: this._patch.getPriorityManager().getPriority(request.node),
                    metadata: request.metadata,
                    type: request.type,
                    patchInfo: this._patchManager.getPatchInfo(),
                    positionOffset: this._patchManager.getPositionOffset(),
                    positions: request.positions,
                    normals: request.normals,
                    texCoords: request.texCoords,
                    layers: request.layers,
                    surfaceStyles: request.surfaceStyles,
                    indices: request.indices,
                    textureData: request.textureData
                };

                this._currentPendingMemory += request.getSize();

                worker.schedulePostMessage(message);
            }
        }

        for (let i = 0; i < aNodesToRemove.length; i++) {
            let removedNode = aNodesToRemove[i];

            if (removedNode.getState() >= ChunkLODNodeState.COMMITTEDTOGPU) {
                worker.schedulePostMessage({
                    uid: removedNode.getUID(),
                    type: "removeFromGpu",
                    patchInfo: this._patchManager.getPatchInfo()
                });
            }

            let request = this._createGPUResourceRequestsMap[removedNode.getUID()];
            if (request !== undefined) {
                this._currentPendingMemory -= request.getSize();
                this.removeRequest(request);
            }

            removedNode.setState(ChunkLODNodeState.REMOVED);
        }
    }
}
